import { useState, useEffect } from 'react';
import { FaCookieBite, FaCheck, FaInfoCircle, FaLock } from 'react-icons/fa';

export default function CookiesConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [animation, setAnimation] = useState('');

  useEffect(() => {
    // Check if notice has already been acknowledged
    const hasAcknowledged = localStorage.getItem('cookieAcknowledged');
    if (!hasAcknowledged) {
      // Only show after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
        setAnimation('animate-slide-up');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acknowledgeAndContinue = () => {
    setAnimation('animate-slide-down');
    setTimeout(() => {
      localStorage.setItem('cookieAcknowledged', 'true');
      setIsVisible(false);
    }, 500);
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed bottom-6 left-0 right-0 z-50 mx-auto px-4 max-w-4xl ${animation}`}>
      <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden p-6 border border-gray-100">
        {/* Decorative elements */}
        <div className="absolute -top-12 -right-12 w-32 h-32 bg-blue-50 rounded-full" />
        <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-primary-50 rounded-full" />
        
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex items-center justify-center p-4 bg-primary-950 rounded-xl">
              <FaCookieBite size={24} className="text-white" />
            </div>
            
            <div className="flex-1">
              {/* <h3 className="text-xl font-bold text-gray-800">Authentication Required</h3> */}
              <p className="text-gray-600 mt-1">
                By continuing to use this site, you accept our use of a cookie to keep you signed in and secure.
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 mt-4 md:mt-0">
              <button
                onClick={toggleDetails}
                className="text-primary-600 text-sm font-medium hover:text-primary-800 flex items-center"
              >
                <FaInfoCircle className="mr-1" />
                {showDetails ? 'Less Info' : 'More Info'}
              </button>
              
              <button
                onClick={acknowledgeAndContinue}
                className="px-5 py-2.5 text-sm font-medium text-white bg-primary-950 rounded-lg hover:bg-primary-900 shadow-md hover:shadow-lg transition-all flex items-center"
              >
                <FaCheck className="mr-1.5" /> Continue
              </button>
            </div>
          </div>
          
          {showDetails && (
            <div className="mt-6 bg-blue-50 rounded-xl p-5 border border-blue-100 relative overflow-hidden">
              <div className="absolute -top-6 -right-6 w-16 h-16 bg-blue-100 rounded-full opacity-70" />
              <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-primary-100 rounded-full opacity-70" />
              
              <div className="relative flex items-start gap-4">
                <div className="flex-shrink-0 p-3 bg-gradient-to-br from-teal-400 to-teal-500 rounded-lg shadow-md">
                  <FaLock size={18} className="text-white" />
                </div>
                
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-800 mb-2">Essential Authentication Cookie</h4>
                  <p className="text-gray-600 text-sm">
                    We use a single essential cookie that contains your JWT (JSON Web Token) for authentication.
                    This allows us to maintain your secure login session and is necessary for the proper functioning of our site.
                  </p>
                  
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mt-4">
                    <h5 className="font-medium text-gray-800 mb-2">Why We Need This</h5>
                    <ul className="text-sm text-gray-600 space-y-2">
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                        To securely authenticate your account and maintain your login session
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                        To protect your account from unauthorized access
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                        To provide you with a seamless user experience without requiring repeated logins
                      </li>
                    </ul>
                  </div>
                  
                  <p className="text-sm text-gray-600 mt-4">
                    This is an essential cookie required for our site to function properly. We do not use any tracking 
                    or marketing cookies. You can learn more about how we handle your data in our Privacy Policy.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}